import React, { useState, useEffect, useRef } from 'react';
import { Users, IndianRupee, UserPlus, Plus, Search, Trash2, Pencil, MoreVertical, Download, Loader2, Code, Copy, Check, ChevronDown, ChevronUp, AlertCircle, Clock, CreditCard, AlertTriangle, Upload, FileText, Info, ArrowUpDown, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerDashboard = () => {
  const [stats, setStats] = useState({ clients: 0, revenue: 0, subAgents: 0 });
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [downloadingVault, setDownloadingVault] = useState(null);
  const [embedSettings, setEmbedSettings] = useState(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [embedExpanded, setEmbedExpanded] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState(null);
  const [isWalkin, setIsWalkin] = useState(false);
  const [onboardingPaid, setOnboardingPaid] = useState(true);
  const [onboardingFee, setOnboardingFee] = useState(1999);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [unpaidDrafts, setUnpaidDrafts] = useState(0);
  const csvFileInputRef = useRef(null);

  const partnerId = localStorage.getItem('partnerId');
  const partnerToken = localStorage.getItem('partnerToken');
  const navigate = useNavigate();

  const axiosConfig = {
    headers: { Authorization: `Bearer ${partnerToken}` }
  };

  // Fetch partner profile to get status and onboarding status
  const fetchPartnerProfile = async () => {
    try {
      const response = await axios.get(`${API}/partner/profile`, axiosConfig);
      setPartnerStatus(response.data.status || 'Active');
      setIsWalkin(response.data.is_walkin || false);
      setOnboardingPaid(response.data.onboarding_paid !== false); // Default to true if not set
      localStorage.setItem('partnerStatus', response.data.status || 'Active');
    } catch (error) {
      console.error('Failed to fetch partner profile:', error);
    }
  };

  // Fetch onboarding status with fee amount
  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/partner/onboarding-status`, axiosConfig);
      setOnboardingPaid(response.data.onboarding_paid !== false);
      setOnboardingFee(response.data.onboarding_fee ?? 1999);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    }
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/partner/dashboard/stats`, axiosConfig);
      setStats(response.data);
      // Fetch unpaid drafts count from stats or separately
      if (response.data.unpaid_drafts !== undefined) {
        setUnpaidDrafts(response.data.unpaid_drafts);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch unpaid drafts count
  const fetchUnpaidDrafts = async () => {
    try {
      const response = await axios.get(`${API}/partner/unpaid-drafts`, axiosConfig);
      setUnpaidDrafts(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch unpaid drafts:', error);
    }
  };

  // Fetch clients
  const fetchClients = async (search = '') => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/partner/clients`, { ...axiosConfig, params });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    }
  };

  // Fetch embed settings
  const fetchEmbedSettings = async () => {
    try {
      const response = await axios.get(`${API}/partner/embed-settings`, axiosConfig);
      setEmbedSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch embed settings:', error);
    }
  };

  useEffect(() => {
    fetchPartnerProfile();
    fetchOnboardingStatus();
    fetchStats();
    fetchClients();
    fetchEmbedSettings();
    fetchUnpaidDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copy embed code to clipboard
  const copyEmbedCode = () => {
    if (embedSettings?.embed_code) {
      navigator.clipboard.writeText(embedSettings.embed_code);
      setCopiedEmbed(true);
      toast.success('Embed code copied to clipboard!');
      setTimeout(() => setCopiedEmbed(false), 2000);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchClients(value);
  };

  // Handle form change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Open Add Modal
  const openAddModal = () => {
    setEditingClient(null);
    setFormData({ name: '', email: '', mobile: '' });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      mobile: client.mobile,
    });
    setIsModalOpen(true);
  };

  // Close Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setFormData({ name: '', email: '', mobile: '' });
  };

  // Add or Update client
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    setIsLoading(true);

    try {
      if (editingClient) {
        await axios.put(`${API}/partner/clients/${editingClient.id}`, formData, axiosConfig);
        toast.success('Client updated successfully!');
      } else {
        await axios.post(`${API}/partner/clients`, formData, axiosConfig);
        toast.success('Client onboarded successfully!');
      }
      closeModal();
      fetchClients(searchQuery);
      fetchStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : detail || 'Operation failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete client
  const handleDeleteClient = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    const confirmMsg =
      `Permanently delete ${client?.name || 'this client'}?\n\n` +
      `This will erase:\n` +
      `  • All their wills and will drafts\n` +
      `  • All vault entries and uploaded files\n` +
      `  • All their trusted contacts\n\n` +
      `Payment and consent history will be retained for audit but will ` +
      `no longer be linked to an active client.\n\n` +
      `This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/partner/clients/${clientId}`, axiosConfig);
      toast.success('Client and all their data permanently deleted');
      fetchClients(searchQuery);
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete client');
    }
  };

  // Resend welcome email
  const [resendingClientId, setResendingClientId] = useState(null);
  const handleResendWelcome = async (client) => {
    setResendingClientId(client.id);
    try {
      await axios.post(`${API}/partner/clients/${client.id}/resend-welcome`, {}, axiosConfig);
      toast.success(`Welcome email sent to ${client.email}`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to send welcome email';
      toast.error(msg);
    } finally {
      setResendingClientId(null);
    }
  };

  // Download shared vault entries for a client
  const handleDownloadVault = async (client) => {
    setDownloadingVault(client.id);
    
    try {
      const response = await axios.get(
        `${API}/partner/client/${client.id}/shared-vault/download`,
        {
          ...axiosConfig,
          responseType: 'blob'
        }
      );
      
      // Check if there are shared entries
      if (response.data.size === 0) {
        toast.info(`${client.name} hasn't shared any vault entries yet`);
        return;
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${client.name.replace(/\s+/g, '_')}_Shared_Vault.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded shared vault for ${client.name}`);
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.info(`${client.name} hasn't shared any vault entries yet`);
      } else {
        toast.error('Failed to download vault');
      }
    } finally {
      setDownloadingVault(null);
    }
  };

  // CSV Upload Functions
  const handleDownloadCsvTemplate = async () => {
    try {
      const response = await axios.get(`${API}/partner/clients/csv-template`, {
        ...axiosConfig,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setCsvUploading(true);
    try {
      const csvContent = await file.text();
      
      const response = await axios.post(`${API}/partner/clients/bulk-upload`, 
        { csv_data: csvContent },
        axiosConfig
      );
      
      const { success_count, failed_count, errors } = response.data;
      
      if (success_count > 0) {
        toast.success(`Successfully added ${success_count} client(s)`);
        fetchClients();
        fetchStats();
      }
      
      if (failed_count > 0) {
        toast.error(`Failed to add ${failed_count} client(s)`);
        if (errors?.length > 0) {
          console.error('CSV Upload Errors:', errors);
        }
      }
      
      setIsCsvModalOpen(false);
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setCsvUploading(false);
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = '';
      }
    }
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedClients = React.useMemo(() => {
    if (!sortConfig.key) return clients;
    
    return [...clients].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle dates
      if (sortConfig.key.includes('login') || sortConfig.key === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [clients, sortConfig]);

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '—';
    return `₹${amount.toLocaleString()}`;
  };

  const statCards = [
    {
      label: 'No. of Clients',
      value: stats.clients,
      icon: Users,
      color: 'amber',
    },
    {
      label: 'Revenue Generated',
      value: stats.revenue > 0 ? `₹${stats.revenue.toLocaleString()}` : '—',
      icon: IndianRupee,
      color: 'emerald',
    },
    {
      label: 'No. of Sub Agents',
      value: stats.subAgents,
      icon: UserPlus,
      color: 'blue',
    },
  ];

  const colorClasses = {
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  };

  return (
    <div className="space-y-8" data-testid="partner-dashboard">
      {/* Onboarding Payment Banner - Show if not paid */}
      {!onboardingPaid && partnerStatus !== 'Pending' && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-amber-400 font-bold text-xl">Complete Your Onboarding</h3>
                <p className="text-slate-300 mt-1">
                  Pay the one-time onboarding fee to unlock client management and brokerage features.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/partner/billing')}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-6 py-5 text-lg shadow-lg shadow-amber-500/25"
              data-testid="go-to-billing-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pay ₹{onboardingFee?.toLocaleString('en-IN')} Now
            </Button>
          </div>
        </div>
      )}

      {/* Pending Approval Banner */}
      {partnerStatus === 'Pending' && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-amber-400 font-semibold text-lg">Account Pending Approval</h3>
              <p className="text-slate-300 mt-1">
                Your partner account is currently under review. Once approved by our admin team, you will be able to:
              </p>
              <ul className="text-slate-400 mt-2 space-y-1 text-sm">
                <li>• Add and manage clients</li>
                <li>• Generate embed codes for your website</li>
                <li>• Access full dashboard features</li>
              </ul>
              <p className="text-slate-500 text-sm mt-3">
                This typically takes 1-2 business days. You will receive an email once approved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back! Here is your overview.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (partnerStatus === 'Pending') {
                toast.error('Your account is pending approval.');
                return;
              }
              if (!onboardingPaid) {
                toast.error('Please complete the onboarding payment first.');
                return;
              }
              setIsCsvModalOpen(true);
            }}
            className={`border-slate-600 text-slate-300 hover:bg-slate-800 ${(partnerStatus === 'Pending' || !onboardingPaid) ? 'opacity-60 cursor-not-allowed' : ''}`}
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV
          </Button>
          <Button
            onClick={() => {
              if (partnerStatus === 'Pending') {
                toast.error('Your account is pending approval. Please wait for admin to activate your account.');
                return;
              }
              if (!onboardingPaid) {
                toast.error('Please complete the onboarding payment first. Go to Billing to pay.');
                navigate('/partner/billing');
                return;
              }
              openAddModal();
            }}
            className={`font-semibold ${(partnerStatus === 'Pending' || !onboardingPaid) ? 'bg-slate-600 hover:bg-slate-600 cursor-not-allowed opacity-60' : 'bg-amber-500 hover:bg-amber-400'} text-slate-900`}
            data-testid="onboard-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Onboard Client
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const colors = colorClasses[stat.color];
          return (
            <Card key={index} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300" data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-400 text-sm font-medium">{stat.label}</p>
                    <p className="text-3xl font-bold text-white mt-2">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${colors.bg}`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Unpaid Drafts Card */}
      {unpaidDrafts > 0 && (
        <Card className="bg-slate-900/50 border-slate-800 border-l-4 border-l-orange-500" data-testid="unpaid-drafts-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-orange-500/10">
                  <FileText className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm font-medium">Unpaid Draft Wills</p>
                  <p className="text-3xl font-bold text-white mt-1">{unpaidDrafts}</p>
                  <p className="text-slate-500 text-sm mt-2">
                    {unpaidDrafts === 1 
                      ? 'Your client has 1 will in draft that hasn\'t been paid for yet.'
                      : `Your clients have ${unpaidDrafts} wills in draft that haven't been paid for yet.`
                    }
                  </p>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="p-2 rounded-lg bg-slate-800/50">
                        <Info className="w-4 h-4 text-slate-400" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="bg-slate-800 border-slate-700 text-white max-w-xs">
                      <p className="text-sm">
                        Clients can create up to 10 draft wills without paying. Payment is required when they proceed to the final step (Declaration & Storage).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Embed Code Card - Collapsible */}
      {embedSettings?.is_configured && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="embed-code-card">
          <CardHeader 
            className="border-b border-slate-800 cursor-pointer hover:bg-slate-800/30 transition-colors"
            onClick={() => setEmbedExpanded(!embedExpanded)}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Code className="w-5 h-5 text-purple-400" />
                </div>
                Embed Code
                <span className="text-sm font-normal text-slate-500">
                  ({embedSettings.embed_domain})
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyEmbedCode();
                  }}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="copy-embed-btn"
                >
                  {copiedEmbed ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-emerald-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
                <div className="p-2 text-slate-400">
                  {embedExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          {embedExpanded && (
            <CardContent className="p-6">
              <p className="text-slate-400 text-sm mb-4">
                Embed the WillKart client portal on your website. Pick the layout that
                fits your page best. All snippets only work from{' '}
                <span className="text-purple-400 font-mono">{embedSettings.embed_domain}</span>.
              </p>

              <EmbedSnippetPicker
                snippets={embedSettings.embed_snippets}
                fallbackCode={embedSettings.embed_code}
              />

              <div className="flex items-center gap-2 text-slate-500 text-sm mt-4">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                <span>Rate limit: {embedSettings.rate_limit} requests/minute</span>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Clients Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white">Clients</CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-9 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="client-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">No clients yet</p>
              <p className="text-slate-500 text-sm mt-1">Click Onboard Client to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center gap-1">
                        Email
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-left p-4 text-slate-400 font-medium">Mobile</th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('first_login')}
                    >
                      <div className="flex items-center gap-1">
                        First Login
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('last_login')}
                    >
                      <div className="flex items-center gap-1">
                        Last Login
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('total_revenue')}
                    >
                      <div className="flex items-center gap-1">
                        Revenue
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('vault_entries_count')}
                    >
                      <div className="flex items-center gap-1">
                        Vault Entries
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th 
                      className="text-left p-4 text-slate-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort('draft_wills_count')}
                    >
                      <div className="flex items-center gap-1">
                        Draft Wills
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedClients.map((client) => (
                    <tr
                      key={client.id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                            <span className="font-semibold text-amber-400">
                              {client.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-white font-medium">{client.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-300 text-sm">{client.email}</td>
                      <td className="p-4 text-slate-300 text-sm">{client.mobile}</td>
                      <td className="p-4 text-slate-400 text-sm">{formatDate(client.first_login)}</td>
                      <td className="p-4 text-slate-400 text-sm">{formatDate(client.last_login)}</td>
                      <td className="p-4 text-emerald-400 text-sm font-medium">{formatCurrency(client.total_revenue)}</td>
                      <td className="p-4 text-slate-300 text-sm">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 cursor-help">
                                <span>{client.vault_entries_count || 0}</span>
                                {client.vault_entries_count > 0 && (
                                  <Info className="w-3 h-3 text-slate-500" />
                                )}
                              </div>
                            </TooltipTrigger>
                            {client.vault_entries_breakdown && Object.keys(client.vault_entries_breakdown).length > 0 && (
                              <TooltipContent className="bg-slate-800 border-slate-700">
                                <div className="text-xs space-y-1">
                                  <p className="font-semibold text-white mb-1">Breakdown by Category</p>
                                  {Object.entries(client.vault_entries_breakdown).map(([cat, count]) => (
                                    <div key={cat} className="flex justify-between gap-4">
                                      <span className="text-slate-400 capitalize">{cat.replace(/_/g, ' ')}</span>
                                      <span className="text-white">{count}</span>
                                    </div>
                                  ))}
                                </div>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="p-4 text-slate-300 text-sm" data-testid={`draft-wills-${client.id}`}>
                        {client.draft_wills_count > 0 ? (
                          <span className="text-orange-400 font-medium">{client.draft_wills_count}</span>
                        ) : (
                          <span className="text-slate-500">0</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-white hover:bg-slate-800"
                              data-testid={`client-actions-${client.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                            <DropdownMenuItem
                              onClick={() => openEditModal(client)}
                              className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadVault(client)}
                              disabled={downloadingVault === client.id}
                              className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer"
                              data-testid={`download-vault-${client.id}`}
                            >
                              {downloadingVault === client.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download Vault
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => client.first_login ? null : handleResendWelcome(client)}
                              disabled={resendingClientId === client.id || !!client.first_login}
                              className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                              data-testid={`resend-welcome-${client.id}`}
                              title={client.first_login ? 'Client has already activated (logged in). Welcome email no longer applicable.' : 'Resend the welcome email with a fresh login link'}
                            >
                              {resendingClientId === client.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-2" />
                                  {client.first_login ? 'Welcome Email (Activated)' : 'Resend Welcome Email'}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem
                              onClick={() => handleDeleteClient(client.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Client Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingClient ? 'Edit Client' : 'Onboard New Client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4" data-testid="client-form">
            <div>
              <Label htmlFor="name" className="text-slate-300 mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleFormChange}
                placeholder="Enter client name"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                placeholder="Enter email address"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label htmlFor="mobile" className="text-slate-300 mb-2 block">
                Mobile
              </Label>
              <Input
                id="mobile"
                name="mobile"
                type="tel"
                required
                value={formData.mobile}
                onChange={handleFormChange}
                placeholder="Enter mobile number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="client-mobile-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="client-submit-btn"
              >
                {isLoading ? (editingClient ? 'Updating...' : 'Adding...') : (editingClient ? 'Update Client' : 'Onboard Client')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Modal */}
      <Dialog open={isCsvModalOpen} onOpenChange={setIsCsvModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Bulk Upload Clients
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h4 className="text-white font-medium mb-2">Instructions:</h4>
              <ol className="text-slate-400 text-sm space-y-1 list-decimal list-inside">
                <li>Download the CSV template</li>
                <li>Fill in client details (Name, Email, Mobile)</li>
                <li>Upload the completed CSV file</li>
              </ol>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleDownloadCsvTemplate}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <FileText className="w-4 h-4 mr-2" />
                Download CSV Template
              </Button>
              
              <div className="relative">
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <Button
                  onClick={() => csvFileInputRef.current?.click()}
                  disabled={csvUploading}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                >
                  {csvUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV File
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <p className="text-slate-500 text-xs text-center">
              Duplicate emails will be skipped. Invalid rows will be reported.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ---------------------------------------------------------------------------
// EmbedSnippetPicker — 4 layout variants for embedding the client portal
// on the partner's website. Lets the partner preview + copy each.
// ---------------------------------------------------------------------------
const EmbedSnippetPicker = ({ snippets, fallbackCode }) => {
  const variants = React.useMemo(() => {
    if (snippets && typeof snippets === 'object' && Object.keys(snippets).length) {
      return Object.entries(snippets).map(([key, v]) => ({ key, ...v }));
    }
    return fallbackCode
      ? [{ key: 'compact', label: 'Embed code', description: '', code: fallbackCode }]
      : [];
  }, [snippets, fallbackCode]);

  const [activeKey, setActiveKey] = React.useState(
    variants.find(v => v.key === 'auto_resize') ? 'auto_resize' : (variants[0]?.key || '')
  );
  const [copiedKey, setCopiedKey] = React.useState(null);

  const active = variants.find(v => v.key === activeKey);

  const copy = (code, key) => {
    navigator.clipboard.writeText(code);
    setCopiedKey(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  if (!variants.length) return null;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {variants.map((v) => (
          <button
            key={v.key}
            onClick={() => setActiveKey(v.key)}
            data-testid={`embed-variant-${v.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeKey === v.key
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-slate-200'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {active && (
        <div className="bg-slate-800/50 rounded-xl p-4">
          {active.description && (
            <p className="text-slate-400 text-xs mb-3">{active.description}</p>
          )}
          <div className="relative">
            <pre className="bg-slate-950 rounded-lg p-4 overflow-x-auto text-xs max-h-64 overflow-y-auto">
              <code className="text-emerald-400 whitespace-pre-wrap break-all">
                {active.code}
              </code>
            </pre>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(active.code, active.key)}
              className="absolute top-2 right-2 border-slate-600 text-slate-300 hover:bg-slate-800 h-7 px-2 text-xs"
              data-testid={`copy-embed-${active.key}`}
            >
              {copiedKey === active.key ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1 text-emerald-400" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};



export default PartnerDashboard;
